import React, { useEffect, useState } from 'react';
import * as Styled from './UsageSummary.styles';
import { useSelector } from 'react-redux';
import { useGlobalization } from '../../contexts';
import { CAPACITY_ITEM, useIsFeatureEnabled } from '../../utils';
import { useCapacity } from '../../hooks';
import { selectApplicationName } from '../../selectors';
import { RegisteredApplications } from '../../types';
import { LaunchOutlined } from '@mui/icons-material';

export interface UsageSummaryProps {
	usedInProject: number | undefined;
}

const UsageSummary = ({ usedInProject }: UsageSummaryProps) => {
	const { t } = useGlobalization();

	const isCapacityPricingEnabledForAsset = !!useIsFeatureEnabled(
		'i360-asset-team-capacity-pricing',
	);
	const isCapacityPricingEnabledForInsight = !!useIsFeatureEnabled(
		'i360i-release-capacity-pricing',
	);
	const isDetailButtonEnabledForAsset = !!useIsFeatureEnabled(
		'i360a-temp-capacity-detail',
	);
	const isDetailButtonEnabledForInsight = !!useIsFeatureEnabled(
		'i360i-temp-capacity-detail',
	);

	const [isLoading, setIsLoading] = useState<boolean>(true);

	const applicationName = useSelector(selectApplicationName);
	const isAssetApp =
		applicationName === RegisteredApplications.APP_AM_MAIN ||
		applicationName === RegisteredApplications.APP_AM_ASSET_UPLOAD;

	const isDetailButtonEnabled =
		(isAssetApp && isDetailButtonEnabledForAsset) ||
		(!isAssetApp && isDetailButtonEnabledForInsight);

	const capacity = useCapacity();
	const currentCapacity = capacity.currentCapacity(
		isAssetApp ? CAPACITY_ITEM.I360A_ASSET : CAPACITY_ITEM.I360I_CHANNEL,
	).data;

	const inUse = currentCapacity?.inUse;
	const total = currentCapacity?.total;
	const overused =
		currentCapacity?.available != undefined &&
		currentCapacity?.available < 0
			? Math.abs(currentCapacity.available)
			: undefined;
	const available =
		currentCapacity?.available == undefined ||
		currentCapacity?.available > 0
			? currentCapacity?.available
			: 0;
	const percent = currentCapacity?.unlimited
		? 0
		: total && total > 0
		? (available! / total) * 100
		: 0;

	useEffect(() => {
		if (isAssetApp && !isCapacityPricingEnabledForAsset) {
			return;
		}
		if (!isAssetApp && !isCapacityPricingEnabledForInsight) {
			return;
		}

		capacity.queryCapacity(
			isAssetApp
				? CAPACITY_ITEM.I360A_ASSET
				: CAPACITY_ITEM.I360I_CHANNEL,
		);
	}, [
		isCapacityPricingEnabledForAsset,
		isCapacityPricingEnabledForInsight,
		isAssetApp,
	]);

	useEffect(() => {
		if (currentCapacity) {
			setIsLoading(false);
		}
	}, [currentCapacity]);

	if (isLoading || currentCapacity?.unlimited) return null;

	return (
		<Styled.Wrapper variant="outlined" elevation={0}>
			<Styled.Container>
				<Styled.HeaderRow>
					<Styled.Header>{t('Usage summary')}</Styled.Header>
					{isDetailButtonEnabled && (
						<Styled.Button
							variant="outlined"
							endIcon={<LaunchOutlined fontSize="small" />}>
							{t('View usage details')}
						</Styled.Button>
					)}
				</Styled.HeaderRow>

				<Styled.ProductTitle>
					{isAssetApp ? t('Info360 Asset') : t('Info360 Insight')}
				</Styled.ProductTitle>

				<Styled.Row>
					<Styled.LeftBlock>
						<Styled.UsageText $isZero={available === 0}>
							<strong>
								{available !== undefined
									? available.toLocaleString()
									: '-'}
							</strong>{' '}
							{available === 0
								? t('available') // "0 available"
								: t('of {{total}} available ({{percent}}%)', {
										total:
											total !== undefined
												? total.toLocaleString()
												: '-',
										percent: percent.toFixed(2),
								  })}
							{overused != undefined && overused > 0 && (
								<>
									<Styled.Separator aria-hidden="true">
										|
									</Styled.Separator>
									<strong>{overused.toLocaleString()}</strong>{' '}
									{t('overused')}
								</>
							)}
							{available === 0 && (
								<Styled.CapIcon
									aria-label={t('No balance available')}
									titleAccess={t('No balance available')}
								/>
							)}
						</Styled.UsageText>
						<Styled.BarInline>
							<Styled.ProgressWrapper>
								<Styled.ProgressFill
									percent={percent}
									aria-label={t('usage progress')}
								/>
							</Styled.ProgressWrapper>

							<Styled.UnitInline>
								{t('Unit: {{unit}}', {
									unit: isAssetApp
										? t('Asset')
										: t('Channel'),
								})}
							</Styled.UnitInline>
						</Styled.BarInline>
					</Styled.LeftBlock>

					<Styled.Stats>
						<Styled.Stat>
							<Styled.StatLabel>
								{t('{{label}} used in this project', {
									label: isAssetApp
										? t('Assets')
										: t('Channels'),
								})}
							</Styled.StatLabel>
							<Styled.StatValue>
								{usedInProject != undefined
									? usedInProject.toLocaleString()
									: '-'}
							</Styled.StatValue>
						</Styled.Stat>

						<Styled.Stat>
							<Styled.StatLabel>
								{t('Total {{label}} used (all projects)', {
									label: isAssetApp
										? t('assets')
										: t('channels'),
								})}
							</Styled.StatLabel>
							<Styled.StatValue>
								{inUse != undefined
									? inUse.toLocaleString()
									: '-'}
							</Styled.StatValue>
						</Styled.Stat>

						<Styled.Stat>
							<Styled.StatLabel>
								{t('Available {{label}} balance', {
									label: isAssetApp
										? t('asset')
										: t('channel'),
								})}
							</Styled.StatLabel>
							<Styled.StatValue>
								{available != undefined
									? available.toLocaleString()
									: '-'}
							</Styled.StatValue>
						</Styled.Stat>
					</Styled.Stats>
				</Styled.Row>
			</Styled.Container>
		</Styled.Wrapper>
	);
};

export default UsageSummary;
